import React, { useState, useEffect } from 'react';
import { db, getSettings } from '../lib/database';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { SearchableSelect } from './ui/searchable-select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Upload, X, Save, Plus, Trash2 } from 'lucide-react';

export default function ProductForm({ product, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    category: '',
    model: '',
    specifications: [], // Array of {name: '', value: ''} objects
    notes: '',
    image: '',
    cost: '',
    costCurrency: 'TL',
    kdvPaid: '',
    profit: '',
    profitType: 'percentage',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });
  
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [errors, setErrors] = useState({});
  const [categories, setCategories] = useState([]);
  const [specificationNames, setSpecificationNames] = useState([]);

  useEffect(() => {
    loadSettings();
    loadExistingData();
    
    if (product) {
      setFormData({
        ...product,
        specifications: product.specifications || [],
        updatedAt: new Date().toISOString()
      });
      setImagePreview(product.image || '');
    } else {
      loadDefaultValues();
    }
  }, [product]);

  const loadSettings = async () => {
    try {
      const settingsData = await getSettings();
      setSettings(settingsData);
    } catch (error) {
      console.error('Error loading settings:', error);
    }
  };

  const loadExistingData = async () => {
    try {
      const allProducts = await db.products.toArray();
      
      // Extract unique categories
      const uniqueCategories = [...new Set(
        allProducts.map(p => p.category).filter(Boolean)
      )];
      setCategories(uniqueCategories);
      
      // Extract unique specification names
      const allSpecs = allProducts.flatMap(p => p.specifications || []);
      const uniqueSpecNames = [...new Set(
        allSpecs.map(s => s.name).filter(Boolean)
      )];
      setSpecificationNames(uniqueSpecNames);
    } catch (error) {
      console.error('Error loading existing data:', error);
    }
  };

  const loadDefaultValues = async () => {
    const settingsData = await getSettings();
    setFormData(prev => ({
      ...prev,
      kdvPaid: settingsData.defaultKdvRate || 20,
      profit: settingsData.defaultProfitMargin || 20,
      profitType: settingsData.defaultProfitType || 'percentage'
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const handleNotesChange = (e) => {
    const value = e.target.value;
    handleInputChange('notes', value);
    
    // Auto-resize textarea
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = Math.max(60, textarea.scrollHeight) + 'px';
  };

  const addSpecification = () => {
    setFormData(prev => ({
      ...prev,
      specifications: [...prev.specifications, { name: '', value: '' }]
    }));
  };

  const updateSpecification = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      specifications: prev.specifications.map((spec, i) =>
        i === index ? { ...spec, [field]: value } : spec
      )
    }));
  };

  const removeSpecification = (index) => {
    setFormData(prev => ({
      ...prev,
      specifications: prev.specifications.filter((_, i) => i !== index)
    }));
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const imageData = e.target.result;
        setImagePreview(imageData);
        handleInputChange('image', imageData);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImagePreview('');
    handleInputChange('image', '');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Ürün adı zorunludur';
    }

    if (!formData.cost || parseFloat(formData.cost) <= 0) {
      newErrors.cost = 'Geçerli bir maliyet girin';
    }

    if (!formData.kdvPaid || parseFloat(formData.kdvPaid) < 0) {
      newErrors.kdvPaid = 'Geçerli bir KDV oranı girin';
    }

    if (!formData.profit || parseFloat(formData.profit) <= 0) {
      newErrors.profit = 'Geçerli bir kar marjı girin';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const productData = {
        ...formData,
        cost: parseFloat(formData.cost),
        kdvPaid: parseFloat(formData.kdvPaid),
        profit: parseFloat(formData.profit),
        updatedAt: new Date().toISOString()
      };

      if (product) {
        await db.products.update(product.id, productData);
      } else {
        productData.createdAt = new Date().toISOString();
        await db.products.add(productData);
      }

      onSave();
    } catch (error) {
      console.error('Error saving product:', error);
      alert('Ürün kaydedilirken hata oluştu!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onCancel}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {product ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
          </DialogTitle>
          <DialogDescription>
            Ürün bilgilerini ve fiyatlama detaylarını girin.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Temel Bilgiler</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Ürün Adı *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Ürün adını girin"
                  />
                  {errors.name && <p className="text-sm text-red-500 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <Label htmlFor="brand">Marka</Label>
                  <Input
                    id="brand"
                    value={formData.brand}
                    onChange={(e) => handleInputChange('brand', e.target.value)}
                    placeholder="Marka adını girin"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="category">Kategori</Label>
                  <SearchableSelect
                    value={formData.category}
                    onValueChange={(value) => handleInputChange('category', value)}
                    options={categories}
                    placeholder="Kategori seç veya yeni ekle..."
                  />
                </div>
                <div>
                  <Label htmlFor="model">Model</Label>
                  <Input
                    id="model"
                    value={formData.model}
                    onChange={(e) => handleInputChange('model', e.target.value)}
                    placeholder="Model girin"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="notes">Notlar</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={handleNotesChange}
                  placeholder="Ek notlar (Enter ile yeni satır ekleyebilirsiniz)"
                  className="min-h-[60px] resize-none overflow-hidden"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>

          {/* Specifications */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Özellikler</CardTitle>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addSpecification}
                  className="flex items-center space-x-2"
                >
                  <Plus className="h-4 w-4" />
                  <span>Özellik Ekle</span>
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {formData.specifications.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>Henüz özellik eklenmemiş.</p>
                  <p className="text-sm mt-1">Ürünün özelliklerini eklemek için "Özellik Ekle" butonunu kullanın.</p>
                </div>
              ) : (
                formData.specifications.map((spec, index) => (
                  <div key={index} className="grid grid-cols-5 gap-3 items-end">
                    <div className="col-span-2">
                      <Label>Özellik Adı</Label>
                      <SearchableSelect
                        value={spec.name}
                        onValueChange={(value) => updateSpecification(index, 'name', value)}
                        options={specificationNames}
                        placeholder="Özellik adı seç veya yaz..."
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>Değer</Label>
                      <Input
                        value={spec.value}
                        onChange={(e) => updateSpecification(index, 'value', e.target.value)}
                        placeholder="Değer girin..."
                      />
                    </div>
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => removeSpecification(index)}
                        className="w-full"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Image Upload */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ürün Görseli</CardTitle>
            </CardHeader>
            <CardContent>
              {imagePreview ? (
                <div className="relative">
                  <img 
                    src={imagePreview} 
                    alt="Preview" 
                    className="w-full h-48 object-cover rounded-lg"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={removeImage}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ) : (
                <div className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-6 text-center">
                  <Upload className="h-12 w-12 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground mb-2">Görsel yüklemek için tıklayın</p>
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full"
                  />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cost Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Maliyet Bilgileri</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cost">Net Maliyet *</Label>
                  <Input
                    id="cost"
                    type="number"
                    step="0.01"
                    value={formData.cost}
                    onChange={(e) => handleInputChange('cost', e.target.value)}
                    placeholder="0.00"
                  />
                  {errors.cost && <p className="text-sm text-red-500 mt-1">{errors.cost}</p>}
                </div>
                <div>
                  <Label htmlFor="costCurrency">Para Birimi</Label>
                  <Select value={formData.costCurrency} onValueChange={(value) => handleInputChange('costCurrency', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="TL">₺ TL</SelectItem>
                      <SelectItem value="USD">$ USD</SelectItem>
                      <SelectItem value="EUR">€ EUR</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="kdvPaid">Ödenen KDV (%) *</Label>
                <Input
                  id="kdvPaid"
                  type="number"
                  step="0.1"
                  value={formData.kdvPaid}
                  onChange={(e) => handleInputChange('kdvPaid', e.target.value)}
                  placeholder="20"
                />
                {errors.kdvPaid && <p className="text-sm text-red-500 mt-1">{errors.kdvPaid}</p>}
              </div>
            </CardContent>
          </Card>

          {/* Profit Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Kar Marjı</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="profitType">Kar Türü</Label>
                <Select value={formData.profitType} onValueChange={(value) => handleInputChange('profitType', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Yüzde (%)</SelectItem>
                    <SelectItem value="amount">Sabit Miktar (TL)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="profit">
                  Kar {formData.profitType === 'percentage' ? '(%)' : '(TL)'} *
                </Label>
                <Input
                  id="profit"
                  type="number"
                  step="0.01"
                  value={formData.profit}
                  onChange={(e) => handleInputChange('profit', e.target.value)}
                  placeholder={formData.profitType === 'percentage' ? '20' : '100'}
                />
                {errors.profit && <p className="text-sm text-red-500 mt-1">{errors.profit}</p>}
              </div>
            </CardContent>
          </Card>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            İptal
          </Button>
          <Button onClick={handleSave} disabled={loading}>
            <Save className="h-4 w-4 mr-2" />
            {loading ? 'Kaydediliyor...' : (product ? 'Güncelle' : 'Kaydet')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}